/*
 * Product.ts (AbstractWebCommon)
 *
 * Copyright © 2022 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Alaguvelammal Alagusubbiah, 2022
 *
 * @file Product.ts
 * @author Alaguvelammal Alagusubbiah
 * @copyright 2022 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import { UseSeatQuantityEnum } from "../../enum/ecommerce/products";
import { IProductTranslation } from "./ProductTranslation";

/** Specifies the type of product transaction. */
export enum ProductType
{
	OneShot = "OneShot", /**< Specifies the product is a one-shot transaction. */
	PaidSubscription = "PaidSubscription", /**< Specifies the product is a paid subscription transaction. */
	FreeUniqueRenewable = "FreeUniqueRenewable", /**< Specifies the product is a renewable free unique transaction. */
	FreeUnique = "FreeUnique", /**< Specifies the product is a non-renewable free unique transaction (i.e. trial). */
}

export interface IOption {
  _id?: string; /**< Option id */
  name: string; /**< Option name */
  description: string; /**< Option description */
  amount: number; /**< Option amount */
  percentage: number; /**< Option percentage */
  per_product_qty: boolean; /**< Option per_product_qty */
  tooltipInformation: string; /**< Option information for tooltip */
}

export interface IProduct {
  _id: string; /**< Product id */
  name: string; /**< Product name */
  sku: string; /**< Product sku */
  quantity: number; /**< Product quantity */
  maxQuantity: number; /**< Product maxQuantity */
  image: string; /**< Product image */
  price: number; /**< Product price */
  url: string; /**< Product url */
  custom: boolean; /**< Product custom */
  live: boolean; /**< Product live */
  options: IOption[]; /**< Product options */
  sla: boolean; /**< SLA */
  duration: number /**< Product duration */;
  transactionCount?: number /**< Number of transactions */
  subscriptionPlanID?: string /**< If product has subscription, fill this field with the plan ID from Braintree service*/
  applicationID?: string /**< ApplicationID to tie a product to a specific application */;
  isLoginRequired?: boolean; /**< Is login required to purchase the product */
  createdAt?: Date /**< When the product was created */
  updatedAt?: Date /**< Last date that the product was changed */
  isDeleted: boolean /**< Is product deleted or not */;
  deletedAt: Date /**< Deletion date */;
  type: ProductType; /**< Specifies the type of the product. */
  quantityType: UseSeatQuantityEnum /**< Define whether License integration should use the field seatQuantity or maxQuantity */;
  seatQuantity?: number; /**< Number of seat quantity */
  isSeatBasedBilling?: boolean /**< When true, this (paid subscription) product is billed per seat: the subscription price is product.price × seatCount, and the customer can change the seat count at any time with pro-rated charge/refund. Independent of quantityType, which only governs License provisioning. */;
  minSeatQuantity?: number /**< Minimum number of billed seats for a seat-based subscription. Enforced at purchase and on every seat change. Only meaningful when isSeatBasedBilling is true. */;
  maxSeatQuantity?: number /**< Optional maximum number of billed seats for a seat-based subscription. Unbounded when null/undefined. Only meaningful when isSeatBasedBilling is true. */;
  formattedSKU: string /**< Removed non alpha-numeric characters from product SKU */;
  translationData?: IProductTranslation[] /**< Product translation data */;
  isPaymentSectionHidden: boolean /**< Defines if the payment section should be displayed or hidden during a transaction */;
  isDisabled: boolean /**< Disable state of product. If the product is disabled, it means that it is not yet ready for purchase */;
  isCustomerDetailsSectionHidden: boolean /**< Defines if the customer details section should be displayed or hidden during a transaction */;
  isLicenseAgreementMentionHidden: boolean /**< Hides any mention of the software license agreement on the checkout page, order confirmation page and order confirmation email. The product behavior (license creation, fulfillment) is unchanged. */;
  isSendingOrderConfirmationEmailAsBCCEnable: boolean /**< Defines if we should send the order confirmation email as BCC to sales team */;
  billingFrequency?: number /**< Defines the subscription plan frequency. We retrieve this value pinging Braintree, and we don't display this field in the UI; it's only for the server side */;
  isUnique?: boolean /**< When true, the user may have at most one active subscription of this product (and, if a group is set, none of any other product in the same group). Independent of the type-derived FreeUnique semantics; see isUniqueProduct(). */;
  group?: string | null /**< Optional grouping key. When set, the user may have active subscriptions to at most one product within this group at any time. Compared as a case-sensitive string globally across applications. */;
}

/**
 * @interface ICheckoutOption
 */
export interface ICheckoutOption {
  description: string; /**< Option description */
  value: false; /**< Checkout option value */
}

/** Determines whether the given product is unique product or not.
 *
 * Note: this is the *legacy, type-derived* check. Several existing call sites use it as a
 * synonym for "free trial / free unique product" (VAT, PDF invoice rendering, BCC e-mail
 * behavior, …); changing its meaning would silently affect those flows. For the new
 * uniqueness/group purchase-rule (which lets admins flag a paid product as unique without
 * changing its type), use hasUniqueProductRule() instead.
 */
export function isUniqueProduct(product: Pick<IProduct, "type">)
{
	return product.type == ProductType.FreeUnique || product.type == ProductType.FreeUniqueRenewable;
}

/** Determines whether the given product is subject to the "unique" purchase rule.
 *
 * Unlike isUniqueProduct(), this is the rule used when validating a new purchase: a product
 * counts as unique when either the explicit `isUnique` flag is set, or its type implies
 * uniqueness (FreeUnique / FreeUniqueRenewable). This lets admins mark a paid product as
 * unique without changing the legacy semantics of isUniqueProduct().
 */
export function hasUniqueProductRule(product: Pick<IProduct, "type"> & Partial<Pick<IProduct, "isUnique">>)
{
	return product.isUnique === true || isUniqueProduct(product);
}

/** Reason a purchase was blocked by the uniqueness/group rules. */
export enum PurchaseBlockReason
{
	/** The user already has an active subscription to this same product, and it is marked unique. */
	UniqueProductAlreadyActive = "UniqueProductAlreadyActive",
	/** The user already has an active subscription to a different product in the same group. */
	GroupConflict = "GroupConflict",
}

/** Minimal product shape needed to evaluate purchase eligibility against an active subscription. */
export interface IActiveSubscriptionProduct
{
	_id: string; /**< Product ID of the active subscription. */
	name?: string; /**< Optional human-readable name of the product, used in error messages. */
	type?: ProductType; /**< Product type, used by isUniqueProduct(). */
	isUnique?: boolean; /**< Explicit uniqueness flag of the product. */
	group?: string | null; /**< Group key of the product. */
}

/** Result of a purchase eligibility evaluation. */
export interface IPurchaseEligibility
{
	allowed: boolean; /**< True when the user is allowed to purchase the candidate product. */
	reason?: PurchaseBlockReason; /**< When not allowed, the rule that blocked it. */
	conflictingProductName?: string; /**< When not allowed, the name of the active product that caused the conflict (if known). */
}

/**
 * Pure function evaluating whether a user can purchase the given product, given a list of
 * the products tied to their currently active subscriptions.
 *
 * Rules:
 *   - If the candidate is unique (per isUniqueProduct()) and the user has an active subscription
 *     to the same product, block.
 *   - If the candidate has a group set, the user cannot have an active subscription to any
 *     OTHER product in that same group. (Multiple actives of the *same* non-unique product are
 *     allowed; uniqueness on the candidate is enforced by the first rule.)
 *
 * The function is provider-agnostic; the caller is responsible for determining which
 * subscriptions count as "active" and supplying the product references.
 */
export function canUserPurchaseProduct(
	candidate: Pick<IProduct, "type"> & Partial<Pick<IProduct, "_id" | "isUnique" | "group" | "name">>,
	activeSubscriptionProducts: IActiveSubscriptionProduct[]
): IPurchaseEligibility
{
	const candidateIsUnique = hasUniqueProductRule(candidate);
	const candidateGroup = candidate.group ?? null;
	const candidateID = candidate._id != null ? String(candidate._id) : null;

	for (const active of activeSubscriptionProducts) {
		const sameProduct = candidateID != null && String(active._id) === candidateID;

		if (candidateIsUnique && sameProduct) {
			return {
				allowed: false,
				reason: PurchaseBlockReason.UniqueProductAlreadyActive,
				conflictingProductName: active.name ?? candidate.name
			};
		}

		if (candidateGroup && !sameProduct && (active.group ?? null) === candidateGroup) {
			return {
				allowed: false,
				reason: PurchaseBlockReason.GroupConflict,
				conflictingProductName: active.name
			};
		}
	}

	return { allowed: true };
}

/** Determines whether the given product is a paid product or not. */
export function isPaidProduct(product: Pick<IProduct, "type">)
{
	return product.type == ProductType.OneShot || product.type == ProductType.PaidSubscription;
}

/** Determines whether the given product is a subscription product or not. */
export function isSubscription(product: Pick<IProduct, "type">)
{
	return product.type == ProductType.PaidSubscription || product.type == ProductType.FreeUnique || product.type == ProductType.FreeUniqueRenewable;
}

/** Determines whether the given product is a paid subscription product or not. */
export function isPaidSubscription(product: Pick<IProduct, "type">)
{
	return isPaidProduct(product) && isSubscription(product);
}

/** Determines whether the given product is billed per seat.
 *
 * Seat-based billing only applies to paid subscriptions with the explicit isSeatBasedBilling
 * flag set. When true, the subscription price is product.price × seatCount and the customer may
 * change the seat count at any time (pro-rated). This is independent of quantityType, which only
 * governs how many License seats are provisioned.
 */
export function isSeatBasedBillingProduct(product: Pick<IProduct, "type"> & Partial<Pick<IProduct, "isSeatBasedBilling">>)
{
	return product.isSeatBasedBilling === true && isPaidSubscription(product);
}

/** The effective minimum number of billed seats for a seat-based product (defaults to 1). */
export function getMinSeatQuantity(product: Partial<Pick<IProduct, "minSeatQuantity">>): number
{
	const min = Number(product.minSeatQuantity);
	return Number.isFinite(min) && min > 0 ? Math.floor(min) : 1;
}

/** Validates a requested seat count against a product's min/max bounds.
 * Returns null when valid, otherwise a machine-readable reason. Pure & provider-agnostic. */
export function validateSeatQuantity(
	product: Partial<Pick<IProduct, "minSeatQuantity" | "maxSeatQuantity">>,
	requestedSeats: number
): "not-an-integer" | "below-min" | "above-max" | null
{
	if (!Number.isInteger(requestedSeats) || requestedSeats <= 0) {
		return "not-an-integer";
	}
	if (requestedSeats < getMinSeatQuantity(product)) {
		return "below-min";
	}
	const max = Number(product.maxSeatQuantity);
	if (Number.isFinite(max) && max > 0 && requestedSeats > Math.floor(max)) {
		return "above-max";
	}
	return null;
}